import { isValidElement, type ReactNode } from "react";
import { motion, type HTMLMotionProps } from "framer-motion";
import { ChevronRight, type LucideIcon } from "lucide-react";

// ─────────────────────────────────────────────────────────────────
// Iratation High School OS — surface primitives.
//
// Three exports, because almost every screen needs exactly these:
//
//   Card        — a rounded white surface with the iOS shadow.
//   CardHeader  — a title, optional subtitle, optional trailing
//                 action. Sits inside Card.
//   Row         — a tappable list row with optional icon,
//                 optional trailing chevron, optional delta pill.
//
// Nothing else belongs here. If you find yourself wanting a
// fourth variant — a "CardWithImage", a "CardDanger" — that's
// a signal the variation belongs at the call site, not in this
// file. Composition over configuration.
// ─────────────────────────────────────────────────────────────────

// ─── Card ───────────────────────────────────────────────────────

type CardProps = {
  children: ReactNode;
  /** Optional tap handler. When present, applies the iOS press effect. */
  onPress?: () => void;
  /** Optional entrance stagger. Same semantics as StatCard. */
  index?: number;
  className?: string;
} & Omit<HTMLMotionProps<"div">, "children" | "onClick">;

export function Card({
  children,
  onPress,
  index = 0,
  className = "",
  ...rest
}: CardProps) {
  const interactive = Boolean(onPress);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.3,
          ease: [0.25, 0.46, 0.45, 0.94],
          delay: index * 0.05,
        },
      }}
      onClick={onPress}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onPress?.();
              }
            }
          : undefined
      }
      className={[
        "rounded-ios-lg bg-ios-card shadow-ios",
        interactive ? "ios-pressable cursor-pointer" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

// ─── CardHeader ─────────────────────────────────────────────────

type CardHeaderProps = {
  title: string;
  subtitle?: string;
  /** Trailing element — a link, a badge, a small icon button. */
  trailing?: ReactNode;
};

export function CardHeader({ title, subtitle, trailing }: CardHeaderProps) {
  return (
    <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-2">
      <div className="min-w-0">
        <h2 className="truncate text-ios-headline font-semibold text-ios-label">
          {title}
        </h2>
        {subtitle && (
          <p className="mt-0.5 truncate text-ios-caption-1 text-ios-secondary">
            {subtitle}
          </p>
        )}
      </div>
      {trailing && <div className="shrink-0">{trailing}</div>}
    </div>
  );
}

// ─── Row ────────────────────────────────────────────────────────
//
// The list row. Structurally identical to how iOS composes a
// UITableViewCell: a leading accessory (icon or avatar), a
// content stack (title + subtitle), a trailing stack (value +
// delta), and an optional disclosure chevron.
//
// The row is a <button> when `onPress` is provided and a plain
// <div> otherwise. This matters for screen readers — a row that
// does nothing shouldn't announce itself as interactive.

type RowProps = {
  title: string;
  subtitle?: string;
  /** Leading visual — a Lucide icon (rendered in a tinted square) or any node. */
  leading?: LucideIcon | ReactNode;
  /** Color for the leading icon square. */
  leadingTone?: "blue" | "green" | "orange" | "red" | "purple" | "brand" | "neutral";
  /** Trailing text — a value, a date, a status. */
  trailing?: ReactNode;
  /** Show a disclosure chevron on the right. */
  chevron?: boolean;
  onPress?: () => void;
  /** Whether to draw the hairline separator underneath. */
  separator?: boolean;
  className?: string;
};

const LEADING_TONES = {
  blue:    { fg: "#007AFF", bg: "rgba(0, 122, 255, 0.10)" },
  green:   { fg: "#34C759", bg: "rgba(52, 199, 89, 0.12)" },
  orange:  { fg: "#FF9500", bg: "rgba(255, 149, 0, 0.12)" },
  red:     { fg: "#FF3B30", bg: "rgba(255, 59, 48, 0.12)" },
  purple:  { fg: "#AF52DE", bg: "rgba(175, 82, 222, 0.12)" },
  brand:   { fg: "var(--brand-navy)", bg: "rgba(10, 37, 64, 0.08)" },
  neutral: { fg: "#3C3C43", bg: "rgba(60, 60, 67, 0.08)" },
} as const;

export function Row({
  title,
  subtitle,
  leading,
  leadingTone = "neutral",
  trailing,
  chevron,
  onPress,
  separator = true,
  className = "",
}: RowProps) {
  const t = LEADING_TONES[leadingTone];

  // Is `leading` a Lucide icon component (a function) or a
  // rendered node (an element)? Lucide icons are components, so
  // `typeof === "function"` is the correct discriminator. A
  // rendered <img> or <Avatar> is an object.
  const isIconComponent =
    typeof leading === "function" ||
    (typeof leading === "object" &&
      leading !== null &&
      !isValidElement(leading) &&
      "$$typeof" in leading);
  const LeadingIcon = isIconComponent ? (leading as LucideIcon) : null;
  const leadingNode =
    leading && !isIconComponent ? (leading as ReactNode) : null;

  const inner = (
    <>
      {(LeadingIcon || leadingNode) && (
        <span className="shrink-0">
          {LeadingIcon ? (
            <span
              className="grid h-8 w-8 place-items-center rounded-ios-md"
              style={{ backgroundColor: t.bg, color: t.fg }}
            >
              <LeadingIcon size={16} strokeWidth={2} absoluteStrokeWidth />
            </span>
          ) : (
            leadingNode
          )}
        </span>
      )}

      <span className="min-w-0 flex-1 text-left">
        <span className="block truncate text-ios-body text-ios-label">
          {title}
        </span>
        {subtitle && (
          <span className="mt-0.5 block truncate text-ios-footnote text-ios-secondary">
            {subtitle}
          </span>
        )}
      </span>

      {trailing && (
        <span className="shrink-0 text-ios-subhead text-ios-secondary">
          {trailing}
        </span>
      )}

      {chevron && (
        <ChevronRight
          size={16}
          strokeWidth={2.4}
          className="shrink-0 text-ios-tertiary"
          absoluteStrokeWidth
        />
      )}
    </>
  );

  const base = [
    "flex w-full items-center gap-3 px-4",
    separator ? "hairline-b" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (onPress) {
    return (
      <button
        type="button"
        onClick={onPress}
        className={[base, "ios-pressable py-3"].join(" ")}
      >
        {inner}
      </button>
    );
  }

  return <div className={[base, "py-3"].join(" ")}>{inner}</div>;
}